"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useRef, useState, type FocusEvent } from "react";
import Image from "next/image";
import type { StaticImageData } from "next/image";
import Link from "next/link";
import { ChevronDown, ChevronUp, Menu, X } from "lucide-react";
import { Typography } from "@repo/ui/aops-components/typography";
import { linkTargetProps, type DiscoveryHeaderView, type DiscoveryNavItemView } from "@/lib/discoveryChrome";
import { trackIfProgramLink } from "./DiscoveryHeaderNavUtils";
import styles from "./DiscoveryHeader.module.css";
type Props = {
  data: DiscoveryHeaderView;
  fallbackLogoSrc: string | StaticImageData;
  fallbackLogoHref: string;
};
function hasChildren(item: DiscoveryNavItemView) {
  return item.children.length > 0;
}
export function DiscoveryHeaderNav(t0) {
  const $ = _c(45);
  const {
    data,
    fallbackLogoSrc,
    fallbackLogoHref
  } = t0;
  const items = data.items;
  const topLinks = data.topLinks;
  const logoHref = data.logoHref || fallbackLogoHref;
  const logoAlt = data.logoAlt || "Art of Problem Solving";
  const logoSrc = data.logoUrl || fallbackLogoSrc;
  const [openKey, setOpenKey] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const menuId = useId();
  let t1;
  let t2;
  if ($[0] !== openKey) {
    t1 = () => {
      if (!openKey) {
        return;
      }
      const onPointerDown = event => {
        if (navRef.current && !navRef.current.contains(event.target as Node)) {
          setOpenKey(null);
        }
      };
      const onKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          setOpenKey(null);
        }
      };
      document.addEventListener("pointerdown", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.removeEventListener("pointerdown", onPointerDown);
        document.removeEventListener("keydown", onKeyDown);
      };
    };
    t2 = [openKey];
    $[0] = openKey;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== mobileOpen) {
    t3 = () => {
      if (!mobileOpen) {
        return;
      }
      const onKeyDown_0 = event_1 => {
        if (event_1.key !== "Escape") {
          return;
        }
        setMobileOpen(false);
        mobileToggleRef.current?.focus();
      };
      document.addEventListener("keydown", onKeyDown_0);
      return () => document.removeEventListener("keydown", onKeyDown_0);
    };
    t4 = [mobileOpen];
    $[3] = mobileOpen;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function closeDropdownIfFocusLeft(itemKey, event_2) {
      const next = event_2.relatedTarget;
      if (!(next instanceof Node)) {
        return;
      }
      if (event_2.currentTarget.contains(next)) {
        return;
      }
      setOpenKey(key => key === itemKey ? null : key);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const closeDropdownIfFocusLeft = t5;
  let t6;
  if ($[7] !== topLinks) {
    t6 = topLinks.length > 0 && <nav className={styles.topLinkBar} aria-label="Sites"><ul className={styles.topLinkList} role="list">{topLinks.map(_temp)}</ul></nav>;
    $[7] = topLinks;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== logoAlt || $[10] !== logoSrc) {
    t7 = <Image src={logoSrc} alt={logoAlt} width={178} height={40} className={styles.logo} loading="eager" unoptimized={true} />;
    $[9] = logoAlt;
    $[10] = logoSrc;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== logoHref || $[13] !== t7) {
    t8 = <Link href={logoHref} className={styles.logoLink}>{t7}</Link>;
    $[12] = logoHref;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== items || $[16] !== menuId || $[17] !== openKey) {
    let t10;
    if ($[19] !== menuId || $[20] !== openKey) {
      t10 = item => {
        const children = hasChildren(item);
        const isOpen = openKey === item._key;
        if (!children) {
          const {
            href
          } = item;
          if (!href) {
            return null;
          }
          return <li key={item._key}><a href={href} className={styles.tab} {...linkTargetProps(item.openInNewTab)} onClick={() => trackIfProgramLink(href)}><Typography as="span" variant="buttonMd">{item.label}</Typography></a></li>;
        }
        return <li key={item._key} className={styles.tabItem} onMouseEnter={() => setOpenKey(item._key)} onMouseLeave={() => setOpenKey(key_0 => key_0 === item._key ? null : key_0)} onBlur={event_3 => closeDropdownIfFocusLeft(item._key, event_3)}><button type="button" className={`${styles.tab} ${isOpen ? styles.tabActive : ""}`} aria-expanded={isOpen} aria-controls={`${menuId}-${item._key}`} onClick={() => setOpenKey(isOpen ? null : item._key)}><Typography as="span" variant="buttonMd">{item.label}</Typography>{isOpen ? <ChevronUp size={16} strokeWidth={1.5} aria-hidden={true} /> : <ChevronDown size={16} strokeWidth={1.5} aria-hidden={true} />}</button>{isOpen && <div id={`${menuId}-${item._key}`} className={styles.dropdown} role="region" aria-label={item.label}>{item.panelGroupLabel && <Typography as="p" variant="eyebrow" className={styles.panelGroupLabel}>{item.panelGroupLabel}</Typography>}<ul className={styles.dropdownList} role="list">{item.children.map(_temp2)}</ul></div>}</li>;
      };
      $[19] = menuId;
      $[20] = openKey;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    t9 = items.map(t10);
    $[15] = items;
    $[16] = menuId;
    $[17] = openKey;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[22] !== t9) {
    t10 = <nav className={styles.desktopNav} aria-label="Primary"><ul className={styles.tabList} role="list">{t9}</ul></nav>;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = `${menuId}-mobile`;
  const t12 = mobileOpen ? "Close menu" : "Open menu";
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setMobileOpen(_temp3);
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== mobileOpen) {
    t14 = mobileOpen ? <X size={24} strokeWidth={1.5} aria-hidden={true} /> : <Menu size={24} strokeWidth={1.5} aria-hidden={true} />;
    $[25] = mobileOpen;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== mobileOpen || $[28] !== t11 || $[29] !== t12 || $[30] !== t14) {
    t15 = <button ref={mobileToggleRef} type="button" className={styles.mobileToggle} aria-expanded={mobileOpen} aria-controls={t11} aria-label={t12} onClick={t13}>{t14}</button>;
    $[27] = mobileOpen;
    $[28] = t11;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t10 || $[33] !== t15 || $[34] !== t8) {
    t16 = <div className={styles.bar}>{t8}{t10}{t15}</div>;
    $[32] = t10;
    $[33] = t15;
    $[34] = t8;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== items || $[37] !== menuId || $[38] !== mobileOpen || $[39] !== openKey) {
    t17 = mobileOpen && <nav id={`${menuId}-mobile`} className={styles.mobileNav} aria-label="Primary"><ul className={styles.mobileList} role="list">{items.map(item_0 => {
          if (hasChildren(item_0)) {
            const isOpen_0 = openKey === item_0._key;
            return <li key={item_0._key} className={styles.mobileItem}><button type="button" className={styles.mobileItemButton} aria-expanded={isOpen_0} onClick={() => setOpenKey(isOpen_0 ? null : item_0._key)}><Typography as="span" variant="h5">{item_0.label}</Typography><ChevronDown size={24} strokeWidth={1.5} aria-hidden={true} className={isOpen_0 ? styles.chevronOpen : undefined} /></button>{isOpen_0 && <div className={styles.mobilePanel}>{item_0.panelGroupLabel && <Typography as="p" variant="eyebrow" className={styles.panelGroupLabel}>{item_0.panelGroupLabel}</Typography>}<ul className={styles.mobileSubList} role="list">{item_0.children.map(child_0 => <li key={child_0._key}><a href={child_0.href} className={styles.mobileSubLink} {...linkTargetProps(child_0.openInNewTab)} onClick={() => {
                      trackIfProgramLink(child_0.href);
                      setMobileOpen(false);
                    }}>{child_0.iconUrl && <span className={styles.iconTile}><Image src={child_0.iconUrl} alt="" aria-hidden={true} className={styles.iconImage} width={24} height={24} unoptimized={true} /></span>}<span className={styles.dropdownText}><Typography as="span" variant="buttonMd">{child_0.label}</Typography>{child_0.description && <Typography as="span" variant="bodySmall" className={styles.dropdownDescription}>{child_0.description}</Typography>}</span></a></li>)}</ul></div>}</li>;
          }
          const {
            href: href_0
          } = item_0;
          if (!href_0) {
            return null;
          }
          return <li key={item_0._key} className={styles.mobileItem}><a href={href_0} className={styles.mobileDirectLink} {...linkTargetProps(item_0.openInNewTab)} onClick={() => {
              trackIfProgramLink(href_0);
              setMobileOpen(false);
            }}><Typography as="span" variant="h5">{item_0.label}</Typography></a></li>;
        })}</ul></nav>;
    $[36] = items;
    $[37] = menuId;
    $[38] = mobileOpen;
    $[39] = openKey;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t16 || $[42] !== t17 || $[43] !== t6) {
    t18 = <header className={styles.header} ref={navRef}>{t6}{t16}{t17}</header>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t6;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
}
function _temp3(open) {
  return !open;
}
function _temp2(child) {
  return <li key={child._key}><a href={child.href} className={styles.dropdownLink} {...linkTargetProps(child.openInNewTab)} onClick={() => trackIfProgramLink(child.href)}>{child.iconUrl && <span className={styles.iconTile}><Image src={child.iconUrl} alt="" aria-hidden={true} className={styles.iconImage} width={24} height={24} unoptimized={true} /></span>}<span className={styles.dropdownText}><Typography as="span" variant="buttonMd" className={styles.dropdownLabel}>{child.label}</Typography>{child.description && <Typography as="span" variant="bodySmall" className={styles.dropdownDescription}>{child.description}</Typography>}</span></a></li>;
}
function _temp(link) {
  return <li key={link._key}><a href={link.href} className={[styles.topLink, link.iconUrl ? styles.topLinkWithIcon : "", link.isCurrent ? styles.topLinkCurrent : ""].filter(Boolean).join(" ")} aria-label={link.iconUrl ? link.label : undefined} aria-current={link.isCurrent ? "page" : undefined} {...linkTargetProps(link.openInNewTab)}>{link.iconUrl && <span className={styles.topLinkIcon}><Image src={link.iconUrl} alt="" aria-hidden={true} width={20} height={20} unoptimized={true} /></span>}<Typography as="span" variant="caption" className={styles.topLinkLabel}>{link.label}</Typography></a></li>;
}